import React from 'react'
import { User, Bot, Clock } from 'lucide-react'

export interface Message {
  id: string
  content: string
  role: 'user' | 'assistant'
  timestamp: Date
  provider?: {
    provider: string
    model: string
  }
  error?: string
}

interface ChatMessageProps {
  message: Message
}

export const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const isUser = message.role === 'user'
  
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  return (
    <div className={`flex space-x-3 ${isUser ? 'flex-row-reverse space-x-reverse' : ''}`}>
      {/* Avatar */}
      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
        isUser 
          ? 'bg-blue-600 text-white' 
          : 'bg-gray-200 text-gray-600'
      }`}>
        {isUser ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
      </div>

      {/* Message Content */}
      <div className={`flex-1 max-w-3xl ${isUser ? 'text-right' : ''}`}>
        {/* Message Header */}
        <div className={`flex items-center space-x-2 mb-1 ${isUser ? 'justify-end' : ''}`}>
          <span className="text-sm font-medium text-gray-900">
            {isUser ? 'You' : 'Assistant'}
          </span>
          <div className="flex items-center space-x-1 text-xs text-gray-500">
            <Clock className="w-3 h-3" />
            <span>{formatTime(message.timestamp)}</span>
          </div>
          {!isUser && message.provider && (
            <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
              {message.provider.provider} • {message.provider.model}
            </span>
          )}
        </div>

        {/* Message Bubble */}
        <div className={`inline-block max-w-full rounded-lg px-4 py-2 ${
          isUser
            ? 'bg-blue-600 text-white'
            : message.error
            ? 'bg-red-50 border border-red-200 text-red-800'
            : 'bg-gray-100 text-gray-900'
        }`}>
          {message.error ? (
            <div>
              <div className="font-medium mb-1">Error</div>
              <div className="text-sm">{message.error}</div>
            </div>
          ) : (
            <div className="whitespace-pre-wrap break-words text-sm">
              {/* Enhanced formatting for debug/markdown content */}
              {message.content.includes('**') || message.content.includes('```') ? (
                <div 
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{
                    __html: message.content
                      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                      .replace(/```([\s\S]*?)```/g, '<pre class="bg-gray-100 p-2 rounded text-xs overflow-x-auto"><code>$1</code></pre>')
                      .replace(/\n/g, '<br>')
                  }}
                />
              ) : (
                message.content
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ChatMessage